import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, Download } from "lucide-react";

const errMsg = (e) => e.response?.data?.detail || "Something went wrong";

export function TwoFactorSetupDialog({ open, setOpen, onEnabled }) {
    const [step, setStep] = useState("password");
    const [password, setPassword] = useState("");
    const [setup, setSetup] = useState(null);
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => { if (open) { setStep("password"); setPassword(""); setSetup(null); setCode(""); } }, [open]);

    const start = async () => {
        setBusy(true);
        try { setSetup((await api.post("/auth/2fa/setup", { password })).data); setStep("scan"); }
        catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
    };
    const confirm = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/auth/2fa/setup/confirm", { code });
            toast.success("Two-step login is on");
            setOpen(false);
            onEnabled(data.backup_codes);
        } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-md" data-testid="twofa-setup-dialog">
                <DialogHeader>
                    <DialogTitle>Turn on two-step login</DialogTitle>
                    <DialogDescription>
                        {step === "password" ? "Confirm your password to continue."
                            : "Scan this with Google Authenticator, Authy or Microsoft Authenticator, then enter the 6-digit code it shows."}
                    </DialogDescription>
                </DialogHeader>
                {step === "password" ? (
                    <div className="space-y-3">
                        <Label className="text-xs">Current password</Label>
                        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                               onKeyDown={(e) => e.key === "Enter" && password && start()}
                               data-testid="twofa-setup-password" />
                        <Button className="w-full" onClick={start} disabled={busy || !password} data-testid="twofa-setup-continue">
                            Continue
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="flex justify-center p-4 bg-white rounded-md" data-testid="twofa-qr">
                            <QRCodeSVG value={setup.otpauth_uri} size={200} />
                        </div>
                        <div className="text-xs text-muted-foreground text-center">
                            Can't scan? Enter this key manually:
                            <div className="font-mono text-foreground break-all mt-1 select-all" data-testid="twofa-secret">{setup.secret}</div>
                        </div>
                        <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} maxLength={6}
                               inputMode="numeric" placeholder="123456" className="text-center text-xl tracking-[0.4em] font-mono"
                               data-testid="twofa-setup-code" />
                        <Button className="w-full" onClick={confirm} disabled={busy || code.length !== 6} data-testid="twofa-setup-confirm">
                            Verify & turn on
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export function BackupCodesDialog({ codes, onClose }) {
    const [saved, setSaved] = useState(false);
    useEffect(() => { setSaved(false); }, [codes]);
    if (!codes) return null;
    const text = `WasteHQ owner backup codes (each works once)\n\n${codes.join("\n")}\n`;
    const download = () => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
        a.download = "routehq-backup-codes.txt";
        a.click();
    };
    return (
        <Dialog open onOpenChange={(v) => !v && saved && onClose()}>
            <DialogContent className="max-w-md" data-testid="twofa-codes-dialog">
                <DialogHeader>
                    <DialogTitle>Save your backup codes</DialogTitle>
                    <DialogDescription>
                        If you lose your phone, each code gets you in once. They won't be shown again.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-2 font-mono text-sm p-4 bg-secondary/40 rounded-md" data-testid="twofa-codes-list">
                    {codes.map((c) => <div key={c} className="text-center">{c}</div>)}
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(text); toast.success("Copied"); }}
                            data-testid="twofa-codes-copy"><Copy className="w-4 h-4 mr-1" />Copy</Button>
                    <Button variant="outline" size="sm" onClick={download} data-testid="twofa-codes-download">
                        <Download className="w-4 h-4 mr-1" />Download
                    </Button>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={saved} onCheckedChange={(v) => setSaved(!!v)} data-testid="twofa-codes-saved" />
                    I've saved these codes somewhere safe
                </label>
                <Button onClick={onClose} disabled={!saved} data-testid="twofa-codes-done">Done</Button>
            </DialogContent>
        </Dialog>
    );
}

export function ReauthDialog({ mode, onClose, onSubmit }) {
    const [password, setPassword] = useState("");
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => { setPassword(""); setCode(""); }, [mode]);
    const go = async () => {
        setBusy(true);
        try { await onSubmit(password, code); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
    };
    return (
        <Dialog open={!!mode} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-sm" data-testid="twofa-reauth-dialog">
                <DialogHeader>
                    <DialogTitle>{mode === "disable" ? "Turn off two-step login" : "Generate new backup codes"}</DialogTitle>
                    <DialogDescription>Confirm with your password and a current code (or a backup code).</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <Input type="password" placeholder="Password" value={password}
                           onChange={(e) => setPassword(e.target.value)} data-testid="twofa-reauth-password" />
                    <Input placeholder="6-digit or backup code" value={code} maxLength={12}
                           onChange={(e) => setCode(e.target.value)} data-testid="twofa-reauth-code" />
                    <Button className="w-full" variant={mode === "disable" ? "destructive" : "default"} onClick={go}
                            disabled={busy || !password || code.length < 6} data-testid="twofa-reauth-submit">
                        {mode === "disable" ? "Turn off" : "Generate codes"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
